import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FormInput from 'components/forms/FormInput';
import { CountBadge } from 'modules/warehouse';
import type { SupplierRequestDraftItem } from '../model/types';
import SolarIcon from 'components/icons/SolarIcon';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import CopyTextWrapper from 'components/others/CopyTextWrapper';

interface RequestItemsTableRowProps {
    item: SupplierRequestDraftItem;
    index: number;
    editCountIndex: number | null;
    setEditCountIndex: (index: number | null) => void;
    onSaveCount: (index: number, value: string | number) => void;
    onRemove: (index: number) => void;
    disabled?: boolean;
    showActions?: boolean;
    showReceived?: boolean;
}
 
const RequestItemsTableRow = ({
    item,
    index,
    editCountIndex,
    setEditCountIndex,
    onSaveCount,
    onRemove, 
    disabled = false,
    showActions = true,
    showReceived = false,
}: RequestItemsTableRowProps) => {
    const { t } = useTranslation(['Vendors', 'Products']);
    const received = Number(item.receivedCount || 0);
    const objectTitle = item.objectName || item.clientName || '';
    const objectSub =
        item.objectName && item.clientName && item.objectName !== item.clientName
            ? item.clientName
            : '';
    const dimensionLabel = tDirName(t, item.dimensionI18nKey, item.dimensionName);

    return (
        <tr>
            <td className="name" data-label={t('supplierRequests.create.colName')}>
                {item.prodName}
            </td>
            <td className="article text-truncate" data-label={t('supplierRequests.create.colArticle')}>
                <CopyTextWrapper value={item.prodArticle || ''}>
                    <span title={item.prodArticle}>{item.prodArticle}</span>
                </CopyTextWrapper>
            </td>
            <td className="object" data-label={t('supplierRequests.create.colObject')}>
                {objectTitle ? (
                    <div className="min-w-0">
                        <div className="text-truncate" title={objectTitle}>
                            {objectTitle}
                        </div>
                        {Number(item.onlySale) ? (
                            <div className="text-muted f-12 text-truncate">
                                {t('supplierRequests.create.modeSale')}
                                {objectSub ? ` · ${objectSub}` : ''}
                            </div>
                        ) : objectSub ? (
                            <div className="text-muted f-12 text-truncate" title={objectSub}>
                                {objectSub}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <span className="text-muted">—</span>
                )}
            </td>
            <td className="count text-end" data-label={t('supplierRequests.create.colCount')}>
                {editCountIndex === index && !disabled ? (
                    <FormInput
                        compact
                        showForm
                        setShowForm={(show) => {
                            if (!show) setEditCountIndex(null);
                        }}
                        defaultValue={Number(item.itemCount || 0)}
                        decimalPlaces={0}
                        callBack={(value) => onSaveCount(index, value)}
                    />
                ) : (
                    <span
                        className={disabled ? '' : 'cursor-pointer'}
                        onClick={() => {
                            if (!disabled) setEditCountIndex(index);
                        }}
                    >
                        <CountBadge count={item.itemCount} /> 
                    </span>
                )}
            </td>
            <td className="dimension text-end" data-label={t('supplierRequests.create.colDimension')}>
                <span className='pe-1'>{dimensionLabel}</span>
            </td>
            {showReceived && (
                <td
                    className="count text-end"
                    data-label={t('supplierRequests.create.colReceived')}
                >
                    <CountBadge count={received} />
                </td>
            )}
            {showActions && (
                <td className="action text-end" data-label={t('supplierRequests.create.colActions')}>
                    <Button
                        type="button"
                        size="sm"
                        className="p-1 py-0"
                        variant="light-danger"
                        disabled={disabled}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            onRemove(index);
                        }}
                    >
                        <SolarIcon name="trash3" />
                    </Button>
                </td>
            )}
        </tr>
    );
};

export default RequestItemsTableRow;
